import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import useSWR from 'swr';

import { CarouselProducts } from 'components/Common/Carousel/CarouselProducts';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import Icon from 'components/Common/Icon/Icon';
import useCurrentCountry from 'hooks/useCurrentCountry';
import useEnv from 'hooks/useEnv';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { fetcher } from 'services/fetcher';
import {
  getFormattedFlowAttributeDefinition,
  getFormattedProductDetailToCarouselProduct,
} from 'services/formatter';
import { getUri } from 'services/generic';
import { getMerchActionProducts } from 'services/merchandising';
import {
  DESKTOP_TABLET_MIN,
  MOBILE_MIN,
  TABLET_PORTRAIT_MIN,
} from 'settings/device';
import { pushSearchProductWithoutResultImpressionEvent } from 'services/listing/gtm';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';
import {
  MAX_PRODUCTS_SearchNoResultDecouvrezAussiProduit,
  MAX_PRODUCTS_SearchNoResultVousAimerezProduit,
} from 'settings/merchandising';
import {
  GTM_PAGE_SEARCH,
  GTM_SEARCH_WITHOUT_RESULT,
} from 'settings/gtm/gtmPageView';
import { ProductDetail } from 'types/Controls/ProductSheet';
import { FormattedFlowAttributeType } from 'types/formatter';
import { SearchNoResult } from 'types/SearchNoResult';

import styles from './SearchWithoutResult.module.scss';

/**
 * Content in SearchPage when API returns no result.
 */
export default function SearchWithoutResult({
  searchKeyword = '',
  totalProductCount,
  title,
}: {
  /** Search word entered by user */
  searchKeyword?: string;
  /** Number of products returned by the search  */
  totalProductCount?: number;
  title: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { isUk } = useCurrentCountry();
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });
  const { datetimeApp } = useEnv();
  const { data } = useSWR<SearchNoResult>(
    !totalProductCount
      ? `${process.env.REACT_APP_URL_API_CATALOG}/json/Custom/SearchNoResult.cxml?${datetimeApp}`
      : null,
    fetcher
  );

  const attributes = useMemo(() => {
    return getFormattedFlowAttributeDefinition(
      data?.Header?.Controls[1]?.Attributes
    );
  }, [data?.Header?.Controls[1]?.Attributes]);

  const shoppingIdeaProducts = useMemo(() => {
    return getMerchActionProducts(
      data?.Zones[1]?.Controls[0]?.Actions,
      MAX_PRODUCTS_SearchNoResultVousAimerezProduit
    );
  }, [
    data?.Zones[1]?.Controls[0]?.Actions,
    MAX_PRODUCTS_SearchNoResultVousAimerezProduit,
  ]);

  const alsoDiscoverProducts = useMemo(() => {
    return getMerchActionProducts(
      data?.Zones[2]?.Controls[0]?.Actions,
      MAX_PRODUCTS_SearchNoResultDecouvrezAussiProduit
    );
  }, [
    data?.Zones[2]?.Controls[0]?.Actions,
    MAX_PRODUCTS_SearchNoResultDecouvrezAussiProduit,
  ]);

  useEffect(() => {
    pushSearchProductWithoutResultImpressionEvent();
  }, [searchKeyword]);

  if (totalProductCount) {
    return null;
  }

  const categories = [
    {
      iconName: 'ready-made-clothes',
      label: t('LABEL_READY_MADE_CLOTHES'),
      url: isUk ? '/C-68540-women' : '/C-68540-femme',
    },
    {
      iconName: 'homewear',
      label: t('LABEL_HOMEWEAR'),
      url: isUk ? '/C-68656-nightwear' : '/C-68656-nuit',
    },
    {
      iconName: 'lingerie',
      label: t('LABEL_LINGERIE'),
      url: isUk ? '/C-180347-underwear' : '/C-180347-sous-vetement',
    },
    {
      iconName: 'shoes',
      label: t('LABEL_SHOES'),
      url: isUk ? '/C-68543-footwear' : '/C-68543-chaussures',
    },
    {
      iconName: 'home',
      label: t('LABEL_HOME_HOUSE'),
      url: isUk ? '/C-68544-home' : '/C-68544-maison',
    },
    {
      className: styles.tie,
      iconName: 'men',
      label: t('LABEL_MEN'),
      url: isUk ? '/C-68541-men' : '/C-68541-homme',
    },
    {
      iconName: 'well-being',
      label: t('LABEL_WELL_BEING'),
      url: isUk ? '/C-68545-health---beauty' : '/C-68545-bien-etre',
    },
  ];

  return (
    <div className={styles.noResultsContainer}>
      <GtmPageView title={title} searchTerm={searchKeyword} />
      <div className={styles.header}>
        <div>
          <h1>
            {t('LABEL_NO_RESULTS_SEARCH_PAGE_TITLE', {
              searchKeyword: searchKeyword,
            })}
          </h1>
          <p>{t('LABEL_NO_RESULTS_SEARCH_PAGE_SUBTITLE')}</p>
        </div>
      </div>
      <div className={styles.categories}>
        {categories?.map((category, loop) => (
          <>
            <Link
              className={styles.categoryLink}
              to={getUri(category.url)}
              key={category.label}
            >
              <span />
              <span>
                <Icon
                  className={classNames(styles.icon, category.className)}
                  iconName={category.iconName}
                />
                <span>{category.label}</span>
              </span>
              <ExpandMoreIcon className={styles.expandIcon} />
            </Link>
            {loop === 3 && !isMobileAndPortraitTablet && (
              <div className={styles.lineBreak} />
            )}
          </>
        ))}
      </div>
      {shoppingIdeaProducts.length > 0 && (
        <ErrorBoundary fallback={<></>}>
          <RenderCarousel
            title={t('LABEL_SHOPPING_IDEA')}
            productDetails={shoppingIdeaProducts}
            attributes={attributes}
          />
        </ErrorBoundary>
      )}
      {alsoDiscoverProducts.length > 0 && (
        <ErrorBoundary fallback={<></>}>
          <RenderCarousel
            title={t('LABEL_ALSO_DISCOVER')}
            productDetails={alsoDiscoverProducts}
            attributes={attributes}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

type GtmPageViewProps = {
  title: string;
  searchTerm: string;
};

function GtmPageView({ title, searchTerm }: GtmPageViewProps) {
  useGtmPageView(GTM_PAGE_SEARCH, title, {
    searchTerm: searchTerm,
    searchStatus: GTM_SEARCH_WITHOUT_RESULT,
    searchResultNumber: 0,
    isLoaded: true,
  });

  return null;
}

function RenderCarousel({
  title,
  productDetails,
  attributes,
}: {
  title: string;
  productDetails: ProductDetail[];
  attributes: FormattedFlowAttributeType;
}) {
  const formattedProducts = useMemo(() => {
    return getFormattedProductDetailToCarouselProduct(
      productDetails,
      attributes
    );
  }, [productDetails, attributes]);

  return (
    <CarouselProducts
      title={title}
      products={formattedProducts}
      classes={{
        carousel: {
          wrapper: styles.carouselWrapper,
          title: styles.carouselTitle,
        },
      }}
      splideOptions={{
        mediaQuery: 'min',
        perPage: 2,
        breakpoints: {
          [MOBILE_MIN]: {
            perPage: 3,
          },
          [TABLET_PORTRAIT_MIN]: {
            perPage: 4,
          },
          [DESKTOP_TABLET_MIN]: {
            perPage: 6,
            gap: '16px',
          },
        },
      }}
    />
  );
}
